import * as React from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Check, Download, Send, Shield, Snowflake, X } from 'lucide-react';
import { Callout, PartLabel, WorkspaceButton, WorkspaceNotice } from '@/components/ui/workspace';
import { StatusChip } from '@/components/ui/status-chip';
import { AreaChip } from '@/components/ui/area-chip';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { PersonAvatar } from '@/components/discovery/coverage-bits';
import { plural } from '@/components/discovery/discovery-logic';
import { UNTAGGED_AREA } from '@/components/discovery/findings/findings-logic';
import { useCoverageQuery, useDiscoverySummaryQuery } from '@/lib/api/discovery';
import { useUntaggedPainPointsQuery } from '@/lib/api/discovery-findings';
import { useEngagementRequirementsQuery } from '@/lib/api/engagement-requirements';
import {
  downloadPacket,
  useSignoffQuery,
  type ChecklistItem,
  type SignoffStatus,
} from '@/lib/api/requirements-signoff';
import { saveBlob } from '@/lib/utils/download';
import { cn } from '@/lib/utils/cn';
import {
  CancelSignoffDialog,
  RequestSignoffDialog,
  ResendSignoffDialog,
  WaiveSignoffDialog,
} from './signoff-dialogs';
import {
  canAsk,
  checklistComplete,
  criticalityCounts,
  formatDayTime,
  historyText,
  historyTone,
  reopenedChangesText,
  signoffHeadline,
  signoffStateLine,
} from './signoff-logic';
import { usePacketWait } from './use-packet-wait';

/**
 * Discovery → Sign-off (T-0191; E12-F11; prototype `#signoff`). The
 * computed checklist, the state and its history, who signs and what they
 * sign, and the export lock. The engagement lead or a firm admin requests
 * sign-off (choosing the signatory from the client's people with an
 * email) or waives it with a written reason; both need the complete
 * checklist and freeze a snapshot. Any later change to the requirements
 * reopens a signature or voids a waiver (Decision 54.37). No one signs
 * here: the client signs from their secure link.
 */

type DialogKind = 'request' | 'waive' | 'resend' | 'cancel' | null;

const CARD = 'rounded-card border border-ink-100 bg-paper-0 px-5 py-4';
const ONLY_LEAD = 'Only the engagement lead and firm admins can do this.';

export function SignoffScreen({ engagementId }: { engagementId: string }) {
  const q = useSignoffQuery(engagementId);
  const summary = useDiscoverySummaryQuery(engagementId);
  const [dialog, setDialog] = React.useState<DialogKind>(null);

  if (q.error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8">
        <ErrorMessage error={q.error} />
        <WorkspaceButton onClick={() => void q.refetch()}>Try again</WorkspaceButton>
      </div>
    );
  }
  if (!q.data) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading sign-off…
      </div>
    );
  }

  const s = q.data;
  const lead = summary.data?.lead.map((l) => l.name).join(', ') || null;
  const signer = s.signatory?.full_name ?? 'the signatory';
  return (
    <div className="min-h-0 flex-1 overflow-auto px-6 pb-8 pt-4">
      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-4">
          <StateCard engagementId={engagementId} s={s} lead={lead} onDialog={setDialog} />
          <History s={s} />
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <WhatTheySign engagementId={engagementId} areas={summary.data?.kpis.areas ?? null} />
          <section aria-label="Client signatory" className={CARD}>
            <PartLabel>Client signatory</PartLabel>
            {s.signatory ? (
              <div className="flex items-center gap-2.5">
                <PersonAvatar id={s.signatory.person_id} name={s.signatory.full_name} size={32} />
                <div className="min-w-0">
                  <div className="font-semibold text-ink-900">{s.signatory.full_name}</div>
                  <div className="text-meta text-ink-500">
                    {[s.signatory.title, s.signatory.email].filter(Boolean).join(' · ')}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-body-sm text-ink-700">
                Chosen when sign-off is requested, from the client’s people with an email.
              </p>
            )}
            <p className="mt-2 text-meta text-ink-500">
              Signs from a secure link, with no account. Their name, the time and the link’s email
              are recorded. A consultant cannot type a signature for them.
            </p>
          </section>
          <ExportCard engagementId={engagementId} unlocked={s.export_unlocked} />
        </div>
      </div>

      {dialog === 'request' ? (
        <RequestSignoffDialog
          engagementId={engagementId}
          status={s}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'waive' ? (
        <WaiveSignoffDialog engagementId={engagementId} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'resend' ? (
        <ResendSignoffDialog
          engagementId={engagementId}
          signatory={signer}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'cancel' ? (
        <CancelSignoffDialog
          engagementId={engagementId}
          signatory={signer}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}

function StateCard({
  engagementId,
  s,
  lead,
  onDialog,
}: {
  engagementId: string;
  s: SignoffStatus;
  lead: string | null;
  onDialog: (d: DialogKind) => void;
}) {
  const head = signoffHeadline(s);
  const ready = canAsk(s) && s.can_lead;
  const again =
    s.state === 'reopened' || s.state === 'changes_requested' || s.state === 'cancelled';
  const whyNot = !s.can_lead
    ? ONLY_LEAD
    : checklistComplete(s)
      ? undefined
      : 'Finish the open conditions first';
  return (
    <section aria-labelledby="signoff-title" className={CARD}>
      <div className="mb-1 flex flex-wrap items-center gap-2.5">
        <h2 id="signoff-title" className="text-h2 font-semibold text-ink-900">
          Requirements sign-off
        </h2>
        <StatusChip tone={head.tone}>{head.chip}</StatusChip>
      </div>
      <div className="text-meta text-ink-500">{signoffStateLine(s)}</div>

      {s.state === 'reopened' ? <ReopenedNotice s={s} /> : null}
      {s.state === 'changes_requested' && s.changes_comment ? (
        <WorkspaceNotice tone="at-risk" className="mt-3">
          <b className="font-semibold text-ink-900">
            What {s.signatory?.full_name ?? 'they'} asked:
          </b>{' '}
          <span className="whitespace-pre-wrap">{s.changes_comment}</span>
        </WorkspaceNotice>
      ) : null}
      {s.state === 'waived' && s.waiver ? (
        <WorkspaceNotice tone="at-risk" className="mt-3">
          <b className="font-semibold text-ink-900">Waiver reason:</b> {s.waiver.reason}
          <div className="text-meta text-ink-500">Printed on every exported packet.</div>
        </WorkspaceNotice>
      ) : null}

      <ul aria-label="Sign-off conditions" className="mt-3 border-t border-ink-100">
        {s.checklist.map((c) => (
          <CheckRow key={c.key} engagementId={engagementId} c={c} />
        ))}
      </ul>

      <div className="mt-4 flex flex-wrap gap-2">
        {s.state === 'requested' ? (
          <>
            <WorkspaceButton
              size="md"
              disabled={!s.can_lead}
              title={s.can_lead ? undefined : ONLY_LEAD}
              onClick={() => onDialog('resend')}
            >
              Resend link
            </WorkspaceButton>
            <WorkspaceButton
              size="md"
              variant="tertiary"
              disabled={!s.can_lead}
              title={s.can_lead ? undefined : ONLY_LEAD}
              onClick={() => onDialog('cancel')}
            >
              Cancel request
            </WorkspaceButton>
          </>
        ) : s.state === 'signed' || s.state === 'waived' ? (
          <PacketButton engagementId={engagementId} label="Signed packet (PDF)" />
        ) : (
          <>
            <WorkspaceButton
              size="md"
              variant="primary"
              disabled={!ready}
              title={whyNot}
              onClick={() => onDialog('request')}
            >
              <Send className="h-3.5 w-3.5" aria-hidden="true" />
              {again ? 'Request sign-off again' : 'Request client sign-off'}
            </WorkspaceButton>
            <WorkspaceButton
              size="md"
              variant="tertiary"
              disabled={!ready}
              title={
                !s.can_lead
                  ? ONLY_LEAD
                  : ready
                    ? undefined
                    : 'A waiver replaces only the client’s signature: finish the open conditions first'
              }
              onClick={() => onDialog('waive')}
            >
              Waive sign-off…
            </WorkspaceButton>
          </>
        )}
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-meta text-ink-500">
        <Shield className="h-3 w-3 flex-none" aria-hidden="true" />
        Only the engagement lead{lead ? ` (${lead})` : ''} and firm admins can request or waive
        sign-off.
      </div>
    </section>
  );
}

/** One condition (`.check-item`): done or not, what it counts, and where to fix it. */
function CheckRow({ engagementId, c }: { engagementId: string; c: ChecklistItem }) {
  const navigate = useNavigate();
  const coverage = useCoverageQuery(engagementId, c.target.kind === 'coverage' && !c.ok);
  // A proposed pain point with no active area is listed only under "Not
  // tagged to an area" on Findings: land there while one is waiting.
  const loose = useUntaggedPainPointsQuery(engagementId, c.target.kind === 'findings' && !c.ok);
  const untaggedToDecide = (loose.data ?? []).filter((p) => p.status === 'proposed').length;
  const params = { engagementId };
  const go = () => {
    switch (c.target.kind) {
      case 'coverage':
        void navigate({ to: '/engagements/$engagementId/discovery', params });
        break;
      case 'sessions':
        void navigate({ to: '/engagements/$engagementId/discovery/sessions', params });
        break;
      case 'findings':
        void navigate({
          to: '/engagements/$engagementId/discovery/findings',
          params,
          ...(untaggedToDecide > 0 ? { search: { area: UNTAGGED_AREA } } : {}),
        });
        break;
      case 'requirements':
        void navigate({ to: '/engagements/$engagementId/selection', params });
        break;
    }
  };
  const areas = (c.target.area_ids ?? [])
    .map((id) => coverage.data?.areas.find((a) => a.area_id === id))
    .filter((a) => a !== undefined);
  return (
    <li className="grid grid-cols-[20px_1fr_auto] items-start gap-3 border-b border-ink-100 px-1 py-3 text-body-sm">
      <span
        className={cn('pt-0.5', c.ok ? 'text-posture-healthy' : 'text-posture-critical')}
        aria-label={c.ok ? 'Done' : 'Open'}
      >
        {c.ok ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
      </span>
      <div className="min-w-0">
        <button
          type="button"
          onClick={go}
          className="text-left font-medium text-ink-900 hover:underline hover:underline-offset-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          {c.label}
        </button>
        {!c.ok && areas.length > 0 ? (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {areas.map((a) => (
              <AreaChip key={a.area_id} title={a.name}>
                {a.label}
              </AreaChip>
            ))}
          </div>
        ) : null}
        {!c.ok && c.target.kind === 'findings' && untaggedToDecide > 0 ? (
          <div className="mt-1 text-meta text-ink-500">
            {untaggedToDecide === 1
              ? '1 of them is not tagged to an area.'
              : `${untaggedToDecide} of them are not tagged to an area.`}
          </div>
        ) : null}
        {!c.ok && c.key === 'criticality_set' ? (
          <div>
            <Link
              to="/engagements/$engagementId/selection"
              params={params}
              className="text-meta font-medium text-ink-700 underline underline-offset-[3px] hover:text-ink-900"
            >
              Open requirements in Selection
            </Link>
          </div>
        ) : null}
      </div>
      <span className="text-meta text-ink-500">{c.value}</span>
    </li>
  );
}

function History({ s }: { s: SignoffStatus }) {
  if (s.history.length === 0) return null;
  return (
    <section aria-label="History" className={CARD}>
      <PartLabel>History</PartLabel>
      <ul className="relative before:absolute before:bottom-1 before:left-[5px] before:top-1 before:w-px before:bg-ink-200">
        {s.history.map((h) => (
          <li
            key={h.signoff_id}
            className="relative grid grid-cols-[140px_1fr] items-start gap-4 py-1.5 pl-[22px]"
          >
            <span
              className={cn(
                'absolute left-0 top-3 z-[1] h-[11px] w-[11px] rounded-full border-2 bg-paper-0',
                TRAIL_DOT[historyTone(h)],
              )}
            />
            <span className="text-meta text-ink-500">{formatDayTime(h.at)}</span>
            <span className="text-body-sm text-ink-700">{historyText(h)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

const TRAIL_DOT: Record<ReturnType<typeof historyTone>, string> = {
  healthy: 'border-posture-healthy',
  'at-risk': 'border-posture-at-risk',
  critical: 'border-posture-critical',
  unassessed: 'border-ink-300',
};

/** What changed after the snapshot (rule 24): the frozen snapshot itself never changes. */
function ReopenedNotice({ s }: { s: SignoffStatus }) {
  const { items, more } = reopenedChangesText(s.reopened_changes);
  const when = s.signed ? `after ${s.signed.name} signed` : 'after the snapshot';
  return (
    <WorkspaceNotice tone="critical" className="mt-3">
      {items.length
        ? `The requirements changed ${when}: ${items.join(', ')}${more ? `, and ${more} more` : ''}. `
        : `The requirements changed ${when}. `}
      The frozen snapshot is kept unchanged; the new state needs a new signature.
    </WorkspaceNotice>
  );
}

const CRIT_ROWS = [
  ['critical', 'Critical', 'amber'],
  ['desired', 'Desired', 'neutral'],
  ['not_required', 'Not required', 'unassessed'],
  ['not_applicable', 'Not applicable', 'frozen'],
] as const;

/** What the client signs: counted by code from the requirements (as E11 counts them). */
function WhatTheySign({ engagementId, areas }: { engagementId: string; areas: number | null }) {
  const reqs = useEngagementRequirementsQuery(engagementId);
  const counts = reqs.data ? criticalityCounts(reqs.data.items) : null;
  return (
    <section aria-label="What the client signs" className={CARD}>
      <PartLabel>What the client signs</PartLabel>
      {!counts ? (
        reqs.error ? (
          <ErrorMessage error={reqs.error} />
        ) : (
          <Spinner />
        )
      ) : (
        <>
          <div className="mb-2.5 flex items-baseline gap-2">
            <span className="text-[24px] font-semibold leading-[30px] tracking-[-0.01em] text-ink-900 tabular-nums">
              {counts.total.toLocaleString()}
            </span>
            <span className="text-meta text-ink-500">
              requirements across {plural(counts.modules, 'module')}
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            {CRIT_ROWS.map(([key, label, tone]) => {
              const n = counts.by[key];
              return (
                <div key={key} className="flex items-center gap-2">
                  <StatusChip tone={tone} className="w-[116px] justify-center">
                    {label}
                  </StatusChip>
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-100">
                    <i
                      className="block h-full max-w-full rounded-full bg-accent-amber-600"
                      style={{ width: `${counts.total ? (n / counts.total) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="w-7 text-right text-body-sm text-ink-900 tabular-nums">{n}</span>
                </div>
              );
            })}
          </div>
          <p className="mt-2.5 text-meta text-ink-500">
            Plus the discovery basis: {areas !== null ? `${plural(areas, 'area')}, ` : ''}the people
            we heard from, the confirmed evidence, and each requirement’s citations.
          </p>
        </>
      )}
      <Callout tone="tip" icon={Snowflake} className="mt-3">
        Requesting sign-off freezes a snapshot. Any change after that reopens sign-off; the signed
        snapshot itself never changes.
      </Callout>
    </section>
  );
}

function ExportCard({ engagementId, unlocked }: { engagementId: string; unlocked: boolean }) {
  return (
    <section aria-label="Export" className={CARD}>
      <PartLabel>Export</PartLabel>
      {unlocked ? (
        <PacketButton engagementId={engagementId} label="Export requirements packet" primary />
      ) : (
        <WorkspaceButton size="md" disabled title="Locked until sign-off is signed or waived">
          <Shield className="h-3.5 w-3.5" aria-hidden="true" />
          Export requirements packet
        </WorkspaceButton>
      )}
      <div className="mt-1.5 text-meta text-ink-500">
        {unlocked
          ? 'Unlocked.'
          : 'Locked until the client signs, or a lead waives sign-off in writing.'}
      </div>
      {unlocked ? null : (
        <div className="mt-1 text-meta text-ink-500">
          The spreadsheet export in Selection stays available, marked “Draft — not signed off”.
        </div>
      )}
    </section>
  );
}

/**
 * Downloads the vendor-facing packet; the server refuses it unless signed or
 * waived. While the PDF is still being rendered it waits (`usePacketWait`),
 * then offers the file as a button of its own.
 */
function PacketButton({
  engagementId,
  label,
  primary = false,
}: {
  engagementId: string;
  label: string;
  primary?: boolean;
}) {
  const save = (file: { blob: Blob; fileName: string }) => saveBlob(file.fileName, file.blob);
  const { wait, start, reset } = usePacketWait({
    fetchPacket: () => downloadPacket(engagementId),
    onFile: save,
  });
  if (wait.kind === 'ready') {
    return (
      <div className="flex flex-col gap-2">
        <WorkspaceButton
          size="md"
          variant="primary"
          onClick={() => {
            save(wait.file);
            reset();
          }}
        >
          <Download className="h-3.5 w-3.5" aria-hidden="true" />
          Download packet
        </WorkspaceButton>
        <div className="text-meta text-ink-500">The packet is ready.</div>
      </div>
    );
  }
  const busy = wait.kind === 'fetching' || wait.kind === 'preparing';
  return (
    <div className="flex flex-col gap-2">
      <WorkspaceButton
        size="md"
        variant={primary ? 'primary' : 'secondary'}
        disabled={busy}
        onClick={start}
      >
        <Download className="h-3.5 w-3.5" aria-hidden="true" />
        {wait.kind === 'fetching'
          ? 'Preparing…'
          : wait.kind === 'preparing'
            ? 'Preparing the packet…'
            : label}
      </WorkspaceButton>
      {wait.kind === 'preparing' ? (
        <div className="flex items-center gap-2 text-meta text-ink-500">
          <Spinner /> The packet is being prepared. This usually takes under a minute.
        </div>
      ) : null}
      {wait.kind === 'failed' ? <ErrorMessage error={wait.error} /> : null}
    </div>
  );
}
